const wikiPhoto = (file) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=1600`;

const googleMaps = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const osmEmbed = (lat, lon, delta = 0.025) => {
  const minLon = lon - delta;
  const minLat = lat - delta * 0.65;
  const maxLon = lon + delta;
  const maxLat = lat + delta * 0.65;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon},${minLat},${maxLon},${maxLat}&layer=mapnik&marker=${lat},${lon}`;
};

const QUERIES = {
  beach: 'Alum Creek State Park Beach, 3400 Lewis Center Rd, Lewis Center, OH 43035',
  parkOffice: 'Alum Creek State Park Office, 3615 S Old State Rd, Delaware, OH 43015',
  marina: 'Alum Creek State Park Marina, 4000 Hollenback Rd, Lewis Center, OH 43035',
  visitorCenter: 'Alum Creek Visitor Center, 5905 Lewis Center Rd, Lewis Center, OH 43035',
  mtbLot: '6914 Lewis Center Rd, Galena, OH 43021',
};

const PHOTOS = {
  beach: wikiPhoto('Gfp-ohio-alum-creek-state-park-beach-and-sky.jpg'),
  lake: wikiPhoto('Alum Creek Lake 1.jpg'),
  dam: wikiPhoto('Alum Creek Dam 1.jpg'),
  woods: wikiPhoto('Alum Creek State Park in November 1.jpg'),
};

/**
 * Featured guide: beginner-to-moderate hiking at Alum Creek State Park
 * (Lewis Center / Delaware — close to Powell, Polaris, Dublin).
 */
export const ALUM_CREEK_HIKE_SPOTS_POST = {
  id: 'featured-alum-creek-hike-spots',
  slug: 'alum-creek-best-hike-spots-beginner-moderate',
  title: 'Best Alum Creek Hike Spots (Beginner to Moderate Plan)',
  excerpt:
    'The closest real hiking to Powell, Polaris, Lewis Center, and Delaware: Storybook and Park Office for first outings, Hollenback and the multi-purpose trail next, then Rocks and Roots when you are ready — and stay off the mountain-bike loops.',
  publishedAt: '2026-09-06T20:00:00.000Z',
  coverImageUrl: PHOTOS.lake,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Trail Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '~10–15 min', label: 'From Powell · Polaris · Lewis Center' },
        { value: '5 hike trails', label: 'ODNR hiking list (not MTB)' },
        { value: 'Easy → mod', label: '0.5 mi up to 4.1 mi' },
        { value: 'Free', label: 'Sunrise–sunset · no gate fee' },
      ],
    },
    {
      type: 'panel',
      title: 'Why Alum Creek is the local hike',
      paragraphs: [
        'If you live in **Powell, Dublin, Polaris, Lewis Center, or Delaware**, Alum Creek State Park is the reservoir in your backyard. You do not need a Hocking Hills day for a real dirt walk. ODNR lists five **hiking/walking** trails here from easy to moderate.',
        'The one rule that saves the day: **do not start at the COMBO mountain-bike lots** (Phase 1 on Lewis Center Road / Phase 2 on Africa Road). Those loops are built for bikes. This guide is hiking-only parking: beach, park office, marina / Hollenback, and the dam / visitor-center side.',
        'Park hours are **sunrise to sunset**. No gate fee. Pets must be leashed and are not allowed on the swimming beach. After heavy rain, clay sections get slick — turn around rather than trenching the trail.',
      ],
      chips: ['Lewis Center · Delaware', 'Beginner to moderate', 'Hiking only', 'Ohio 2026'],
    },
    {
      type: 'imageGallery',
      title: 'What the park looks like',
      intro:
        'Largest inland beach in the Ohio state-park system, plus woods, dam views, and shoreline. Photos: Wikimedia Commons.',
      images: [
        {
          url: PHOTOS.lake,
          alt: 'Alum Creek Lake reservoir at Alum Creek State Park',
          caption: 'Alum Creek Lake from the park — the hike backdrop on most trails.',
          mapUrl: googleMaps(QUERIES.beach),
        },
        {
          url: PHOTOS.beach,
          alt: 'Alum Creek State Park swimming beach and sky',
          caption: 'West-side beach — Storybook Trail starts at the north end of this shoreline.',
          mapUrl: googleMaps(QUERIES.beach),
        },
        {
          url: PHOTOS.dam,
          alt: 'Alum Creek Dam and reservoir',
          caption: 'Alum Creek Dam — easy dam-top walk for lake views without roots.',
          mapUrl: googleMaps(QUERIES.visitorCenter),
        },
        {
          url: PHOTOS.woods,
          alt: 'Wooded shoreline at Alum Creek State Park in November',
          caption: 'Woods along the reservoir — typical of Hollenback and Park Office trails.',
          mapUrl: googleMaps(QUERIES.parkOffice),
        },
      ],
    },
    {
      type: 'table',
      title: 'Best hike spots (ODNR hiking trails)',
      intro:
        'Distances and grades below follow the Ohio Department of Natural Resources hiking list. Turn around anytime — easy trails are still a success at 30 minutes.',
      headers: ['Trail', 'Trailhead / parking', 'Miles & grade', 'Best for'],
      rows: [
        [
          'Storybook Trail',
          `North end of the swimming beach · 3400 Lewis Center Rd, Lewis Center, OH 43035 · [Map](${googleMaps(QUERIES.beach)})`,
          '0.5 mi · Easy',
          'First hike, kids, strollers on a grass path. Rotating storyboards + Little Free Library. Pair with a beach/picnic stop.',
        ],
        [
          'Park Office Trail',
          `Park office · 3615 S Old State Rd, Delaware, OH 43015 · [Map](${googleMaps(QUERIES.parkOffice)})`,
          '1.5 mi · Easy',
          'Quiet woods loop near the office. Best “just go walk” option if the beach lot is slammed.',
        ],
        [
          'Hollenback Trail',
          `Marina / Hollenback area · 4000 Hollenback Rd, Lewis Center, OH 43035 · [Map](${googleMaps(QUERIES.marina)})`,
          '1.5 mi · Easy',
          'Lake-adjacent woods without bike traffic. Good second hike after Storybook.',
        ],
        [
          'Multi-purpose Trail',
          'Access near the campground / multi-purpose trailhead (check ODNR trail map). Shared path — yield to bikes.',
          'Up to 5 mi · Easy',
          'Flat-ish longer walk. Do 1.5–3 miles out-and-back on outing three; you do not have to finish all 5.',
        ],
        [
          'Rocks and Roots Trail',
          `Common start near the marina · 4000 Hollenback Rd · [Map](${googleMaps(QUERIES.marina)})`,
          '4.1 mi · Moderate',
          'The step-up: more roots and technical footing than hills. Save until the easy trails feel normal. Morning friends plan: [Rocks and Roots 7:00 a.m. hike](/blog/rocks-and-roots-alum-creek-morning-hike-plan).',
        ],
        [
          'Dam / shoreline walk (bonus easy)',
          `USACE visitor center / dam area · 5905 Lewis Center Rd, Lewis Center, OH 43035 · [Map](${googleMaps(QUERIES.visitorCenter)})`,
          '~1–2 mi · Easy',
          'Paved then grass along the dam with big water views — sunset walk, not a gorge hike.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Where to park (and where not to)',
      headers: ['Lot', 'Address', 'Use this for', 'Skip if'],
      rows: [
        [
          'Swimming beach',
          '3400 Lewis Center Rd, Lewis Center, OH 43035',
          'Storybook Trail, shoreline, picnic, swim after',
          'You want solitude on a hot Saturday — this lot fills',
        ],
        [
          'Park office',
          '3615 S Old State Rd, Delaware, OH 43015',
          'Park Office Trail, maps, questions (office 8 a.m.–4:30 p.m. weekdays)',
          'You specifically want beach + storyboards',
        ],
        [
          'Marina / Hollenback',
          '4000 Hollenback Rd, Lewis Center, OH 43035',
          'Hollenback Trail, Rocks and Roots start',
          'You accidentally followed a bike-park pin',
        ],
        [
          'COMBO Phase 1 MTB lot',
          '6914 Lewis Center Rd, Galena, OH 43021',
          'Mountain biking only',
          'You are hiking. Do not walk Gnomewood / Phase 1 / Phase 2 as a hike.',
        ],
      ],
    },
    {
      type: 'mapEmbed',
      title: 'Trailhead maps',
      intro:
        'Three hiking lots on the west/north side of the lake. Open in Google Maps for turn-by-turn from Powell, Polaris, or Delaware.',
      maps: [
        {
          title: 'Beach & Storybook Trail',
          address: '3400 Lewis Center Rd, Lewis Center, OH 43035',
          embedUrl: osmEmbed(40.1845, -82.9755, 0.02),
          directionsUrl: googleMaps(QUERIES.beach),
        },
        {
          title: 'Park office',
          address: '3615 S Old State Rd, Delaware, OH 43015',
          embedUrl: osmEmbed(40.228, -83.012, 0.02),
          directionsUrl: googleMaps(QUERIES.parkOffice),
        },
        {
          title: 'Marina / Hollenback',
          address: '4000 Hollenback Rd, Lewis Center, OH 43035',
          embedUrl: osmEmbed(40.198, -82.958, 0.02),
          directionsUrl: googleMaps(QUERIES.marina),
        },
      ],
    },
    {
      type: 'panel',
      title: 'Beginner-to-moderate plan (four outings)',
      paragraphs: [
        'Do these in order. Same park, more trail skill each time. If an outing feels hard, repeat it before moving up — Alum Creek is 10 minutes from home, not a once-a-year trip.',
      ],
      orderedList: [
        '**Outing 1 — Storybook + beach (45–75 min).** Park at 3400 Lewis Center Rd. Walk the 0.5-mile Storybook Trail, then add as much shoreline as feels easy. Closed-toe shoes, water, tick spray. Goal: leave the lot, stay on marked path, turn around on time.',
        '**Outing 2 — Park Office Trail (60–90 min).** 1.5 miles easy woods at 3615 S Old State Rd. Practice packing 1 liter of water and walking on dirt/roots without rushing.',
        '**Outing 3 — Hollenback or Multi-purpose (75–120 min).** Hollenback (1.5 mi easy) from the marina, **or** 2–3 miles of the multi-purpose trail and back. Share the path; stay right if bikes pass. Goal: a longer easy walk, not a new grade yet.',
        '**Outing 4 — Rocks and Roots (2–3 hours).** 4.1 miles moderate from the marina area. Slow on roots; this is footing, not a mountain. Turn around at halfway if anyone is sliding or tired. After this, you are ready for a day trip like Cemetery Ridge at Clear Creek — still a bigger climb than anything at Alum Creek.',
      ],
      callout:
        'Weekday mornings beat weekend beach traffic. If it rained hard in the last 24 hours, do Outing 1 on firmer ground or wait a day — do not start Rocks and Roots in wet clay.',
    },
    {
      type: 'panel',
      title: 'Sample half-day from Polaris / Powell',
      orderedList: [
        '8:30 a.m. — Park at the beach lot (Lewis Center Rd).',
        '8:40–9:20 — Storybook Trail.',
        '9:20–10:00 — Shoreline walk + water/snack at a picnic table.',
        'Optional: drive 10 minutes to the marina for Hollenback if legs still feel fresh (that becomes Outing 3, not Outing 1).',
        'Home before lunch. Next week, skip the beach and go straight to the park office trail.',
      ],
    },
    {
      type: 'panel',
      title: 'Pack list (keep it light)',
      list: [
        'Closed-toe shoes with tread — even the easy trails have roots after rain.',
        'Water (0.5–1 L on outings 1–2; 1 L+ on Rocks and Roots).',
        'Tick spray, especially late spring through fall in woods and tall grass.',
        'Phone with the ODNR trail map screenshot (link below).',
        'Optional after Outing 1: swimsuit for the beach, or a dry layer if you started early.',
      ],
    },
    {
      type: 'panel',
      title: 'Hike vs bike — do not mix them',
      paragraphs: [
        'Alum Creek is also Central Ohio’s main **mountain-bike** park (Gnomewood, Phase 1, Phase 2). Those trails are directional, bike-built, and a conflict magnet if you walk them. If you want dirt wheels, use our mountain-biking guide and the COMBO lots. If you want hiking, use the lots in this article.',
      ],
    },
  ],
  relatedPostsTitle: 'More local trail guides',
  relatedPosts: [
    {
      slug: 'rocks-and-roots-alum-creek-morning-hike-recap',
      title: 'Rocks and Roots Recap: Alum Creek Morning Hike (7:00–9:30 a.m.)',
      blurb: 'the friends group finished the 7:00 a.m. loop — photos from the trailhead.',
    },
    {
      slug: 'rocks-and-roots-alum-creek-morning-hike-plan',
      title: 'Rocks and Roots Morning Hike Plan (Alum Creek, 7:00 a.m.)',
      blurb: 'shareable 7 a.m. friends plan for the moderate loop.',
    },
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'Highbanks, Shale Hollow, and statewide first hikes.',
    },
    {
      slug: 'cemetery-ridge-clear-creek-hike-prep',
      title: 'How to Prepare for Cemetery Ridge at Clear Creek Metro Park',
      blurb: 'the next-step climb after Alum Creek feels easy.',
    },
    {
      slug: 'mountain-biking-trails-powell-dublin-polaris-delaware-ohio',
      title: 'Best Mountain Biking Trails Near Powell, Dublin, Polaris & Delaware',
      blurb: 'Alum Creek bike loops — different parking, different trails.',
    },
    {
      slug: 'half-day-picnic-near-polaris-ohio-kids-guide',
      title: 'Nature Picnic Day Near Polaris, Ohio',
      blurb: 'pair a short Alum Creek walk with lunch.',
    },
  ],
  referenceLinks: [
    {
      label: 'Ohio DNR — Alum Creek State Park (hours, hiking list, Storybook Trail)',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/alum-creek-state-park',
    },
    {
      label: 'Ohio DNR — Alum Creek hiking map (PDF)',
      url: 'https://dam.assets.ohio.gov/image/upload/ohiodnr.gov/documents/parks/parkmaps/alumcreektrailmap.pdf',
    },
    {
      label: 'Ohio DNR — Alum Creek park map (PDF)',
      url: 'https://dam.assets.ohio.gov/image/upload/ohiodnr.gov/documents/parks/parkmaps/alumcreekparkmap.pdf',
    },
    {
      label: 'Google Maps — Alum Creek swimming beach / Storybook',
      url: googleMaps(QUERIES.beach),
    },
  ],
};
